import { useState } from "react"

import { WARNING_MSG } from "../../../constants/tooltips"
import { SUPPORT_REFER } from "../../../errors/errorMessages"
import { snackbar } from "../../../utils/general/generalUtils"
import { getClientProtected } from "../clients/clients"
import { attachmentMapper } from "../mappers/attachments/attachmentMapper"

interface IDownload {
    downloadAttachment: (api: string) => Promise<void>
    isLoading: boolean
    sensitiveDataWarning?: boolean
}

/**
 * Generic hook for downloading an attachment. Exposes `downloadAttachment`
 * function which is used to call a specified GET attachment API.
 * @returns {IDownload} - loading and error states.
 */

const useDownload = (sensitiveDataWarning = false): IDownload => {
    const [isLoading, setIsLoading] = useState(false)

    /**
     * Downloads a base 64 encoded string
     * @param apiRes - base64 encoded string
     * @param filename - download file name
     * @param ext - download file extension
     */
    const processDownload = (apiRes: unknown): void => {
        try {
            const apiDownloadRes = attachmentMapper.map(apiRes)
            const downloadLink = document.createElement("a")
            downloadLink.href = apiDownloadRes.data_b64
            downloadLink.download = apiDownloadRes.filename
            downloadLink.click()
        } catch {
            snackbar(
                `Unable to download. Please try again. ${SUPPORT_REFER}`,
                "warning"
            )
        }
    }

    /** Generic download function */
    const downloadAttachment = async (api: string): Promise<void> => {
        try {
            setIsLoading(true)
            const res = await getClientProtected(api)
            processDownload(res)
            if (sensitiveDataWarning) {
                snackbar(WARNING_MSG, "default")
            }
        } catch {
            snackbar(
                `Unable to download. Please try again. ${SUPPORT_REFER}`,
                "warning"
            )
        } finally {
            setIsLoading(false)
        }
    }

    return {
        isLoading,
        downloadAttachment,
    }
}

export default useDownload
